import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line
import 'yet-another-react-lightbox/styles.css';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';

// @mui
import {
  Button,
  Card,
  Table,
  Stack,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  CircularProgress,
  TableHead,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { InfoIcon } from 'lucide-react';

import InfoTooltip from '../components/InfoTooltip';
import Scrollbar from '../components/scrollbar';

import {
  getAllCleaningAverages,
  getAllCleaningReport,
  getCleanerSetting,
  getLastBnBCleanigAmount,
} from '../helper/backend_helper';

const TABLE_HEAD = [
  { id: '', label: 'BnB', alignRight: true },
  { id: '', label: 'Average Cleaning Cost', alignRight: true },
  { id: '', label: 'Average Bedsheet Cost', alignRight: true },
  { id: '', label: 'Total + VAT', alignRight: true },
  { id: '', label: 'Cleaning Fee Amount', alignRight: true },
  { id: '', label: 'Cost Difference', alignRight: true },
  { id: '', label: 'Cost Percentage', alignRight: true },
  { id: '', label: 'Dettaglio', alignRight: true },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.name?.toLowerCase()?.indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis?.map((el) => el[0]);
}

export default function CleanerAveragesPage() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [clickedRowData, setClickedRowData] = useState(null);
  const [cleaningItem, setCleaningItem] = useState('');
  const [cleaners, setCleaners] = useState([]);
  const [users, setUsers] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [originalUsers, setOriginalUsers] = useState([]);
  const [endDate, setEndDate] = useState(null);
  const [bnbs, setBnbs] = useState([]);
  const [bnbValue, setBnBValue] = useState('');
  const [cleanerSettings, setCleanerSettings] = useState({});
  const [cleaningAmount, setCleaningAmount] = useState([]);
  const handleStartDateChange = (newValue) => {
    setStartDate(newValue); // Update state when date is selected
  };
  useEffect(() => {
    const hideLicenseKeyMessage = () => {
      // Select all divs
      const messages = document.querySelectorAll('button');
      messages.forEach((message) => {
        if (message.classList.contains('yarl__button')) {
          message.style.margin = '60px'; // Set margin to 40px
        }
      });
    };

    // Run the function after the component mounts
    hideLicenseKeyMessage();

    // Optional: Use MutationObserver to detect changes if the message might reappear
    const observer = new MutationObserver(hideLicenseKeyMessage);
    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup observer on component unmount
    return () => {
      observer.disconnect();
    };
  }, []);
  const handleEndDateChange = (newValue) => {
    setEndDate(newValue); // Update state when date is selected
  };
  async function fetchData() {
    try {
      setLoading(true);
      const usersResponse = await getAllCleaningAverages();
      console.log(usersResponse);
      const filteredUsers = usersResponse?.data?.data;
      setUsers(filteredUsers);
      setOriginalUsers(filteredUsers);
      const settingResponse = await getCleanerSetting();
      setCleanerSettings(settingResponse?.data?.data);
      const cleaningAmountResponse = await getLastBnBCleanigAmount();
      setCleaningAmount(cleaningAmountResponse?.data?.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // fetchBNB();
  }, []);

  const [loading, setLoading] = useState(false);

  const handleOpenMenu = (event, row) => {
    setOpen(event.currentTarget);
    setClickedRowData(row);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users?.length) : 0;

  const filteredUsers = applySortFilter(users, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers?.length && !!filterName;
  // const encodedData = encodeURIComponent(JSON.stringify(clickedRow));

  // Group filteredUsers by bnb
  const groupedByBnb = filteredUsers?.reduce((acc, curr) => {
    const { bnb } = curr;
    if (!acc[bnb]) {
      acc[bnb] = [];
    }
    acc[bnb].push(curr);
    return acc;
  }, {});

  // Convert grouped object into an array for mapping
  const groupedBnbArray = Object.entries(groupedByBnb || {}).sort((a, b) => a[0].localeCompare(b[0]));
  const openBnbReports = (bnbName) => {
    // Open the cleaning report page with this property already selected as a filter.
    navigate(`/cleaners/cleaningReport?bnb=${encodeURIComponent(bnbName)}`);
  };

  const getColorByDifference = (differencePercentage) => {
    const maxDiff = 30; // +/- 30% for full red/green
    const clampedDiff = Math.max(-maxDiff, Math.min(differencePercentage, maxDiff));

    if (clampedDiff < 0) {
      // Red to Yellow
      const ratio = (clampedDiff + maxDiff) / maxDiff; // 0 (red) to 1 (yellow)
      const r = 255;
      const g = Math.round(255 * ratio);
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
      // eslint-disable-next-line
    } else {
      // Yellow to Green
      const ratio = clampedDiff / maxDiff; // 0 (yellow) to 1 (green)
      const r = Math.round(255 * (1 - ratio));
      const g = 255;
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    }
  };
  return (
    <>
      <Helmet>
        <title> Cleaner Average Report </title>
      </Helmet>

      <Card sx={{ padding: 3, marginTop: 3 }}>
        <Stack mb={3} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4">
            Averages{' '}
            <InfoTooltip
              width={20}
              title="A per-property summary of cleaning economics, comparing what cleaners are paid against the cleaning fee charged to guests. For each BnB it averages the reported cleaning and bedsheet costs, adds VAT, and shows the difference and percentage versus the booking cleaning fee, with rows colour-coded green to red by margin. Sort by Cost Difference to spot loss-making properties and click Dettaglio to open that BnB's full cleaning report."
            />
          </Typography>
        </Stack>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              {/* <SolutionListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={users.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
              /> */}

              <TableHead>
                <TableRow>
                  <TableCell style={{ paddingLeft: 13, paddingRight: 13 }} align="left">
                    BnB
                  </TableCell>
                  <TableCell style={{ paddingLeft: 13, paddingRight: 13 }} align="left">
                    Average Cleaning Cost
                  </TableCell>
                  <TableCell style={{ paddingLeft: 13, paddingRight: 13 }} align="left">
                    Average Bedsheet Cost
                  </TableCell>
                  <TableCell style={{ paddingLeft: 13, paddingRight: 13 }} align="left">
                    Total + VAT
                  </TableCell>
                  <TableCell style={{ paddingLeft: 13, paddingRight: 13 }} align="left">
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      Cleaning Fee Amount
                      <Tooltip
                        arrow
                        title="This is the BnB cleaning fee from Cleaning > Cleaning Cost. It uses the booking cleaning fee or mid-stay cleaning fee—if neither exists, the process booking cleaning fee is applied."
                      >
                        <InfoIcon size={18} />
                      </Tooltip>
                    </span>
                  </TableCell>
                  <TableCell style={{ paddingLeft: 13, paddingRight: 13 }} align="left">
                    <TableSortLabel
                      hideSortIcon
                      active={orderBy === 'costDifference'}
                      direction={orderBy === 'costDifference' ? order : 'asc'}
                      onClick={() => handleRequestSort('', 'costDifference')}
                    >
                      Cost Difference
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ paddingLeft: 13, paddingRight: 13 }} align="left">
                    Cost Percentage
                  </TableCell>
                  <TableCell style={{ paddingLeft: 13, paddingRight: 13 }} align="left">
                    Dettaglio
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...groupedBnbArray]
                  .sort((a, b) => {
                    const usersA = a[1];
                    const usersB = b[1];

                    const validCleaningUsersA = usersA.filter(
                      (user) => user.cleaningCost > cleanerSettings?.ignoreAverageCalValue
                    );
                    const validBedsheetUsersA = usersA.filter(
                      (user) => user.bedsheetCost > cleanerSettings?.ignoreAverageCalValue
                    );
                    const totalCleaningCostA = validCleaningUsersA.reduce((acc, curr) => acc + curr.cleaningCost, 0);
                    const totalBedsheetCostA = validBedsheetUsersA.reduce((acc, curr) => acc + curr.bedsheetCost, 0);
                    const avgCleaningCostA =
                      validCleaningUsersA.length > 0 ? totalCleaningCostA / validCleaningUsersA.length : 0;
                    const avgBedsheetCostA =
                      validBedsheetUsersA.length > 0 ? totalBedsheetCostA / validBedsheetUsersA.length : 0;
                    const vatA = 1 + cleanerSettings?.VAT / 100;
                    const totalVatA = (avgBedsheetCostA + avgCleaningCostA) * vatA;

                    // const cleaningCostA =
                    //   cleaningAmount.find((item) => item.BnBShortName === a[0])?.cleaning_fee_amount || 0;

                    const cleaningCostA =
                      usersA?.[0]?.bnbDetails?.bookingCleaningFee > 0
                        ? usersA[0].bnbDetails.bookingCleaningFee
                        : usersA?.[0]?.bnbDetails?.midStayCleaningFee > 0
                        ? usersA[0].bnbDetails.midStayCleaningFee
                        : cleaningAmount.find((item) => item.BnBShortName === a[0])?.cleaning_fee_amount || 0;

                    const costDifferenceA = cleaningCostA - totalVatA;

                    const validCleaningUsersB = usersB.filter(
                      (user) => user.cleaningCost > cleanerSettings?.ignoreAverageCalValue
                    );
                    const validBedsheetUsersB = usersB.filter(
                      (user) => user.bedsheetCost > cleanerSettings?.ignoreAverageCalValue
                    );
                    const totalCleaningCostB = validCleaningUsersB.reduce((acc, curr) => acc + curr.cleaningCost, 0);
                    const totalBedsheetCostB = validBedsheetUsersB.reduce((acc, curr) => acc + curr.bedsheetCost, 0);
                    const avgCleaningCostB =
                      validCleaningUsersB.length > 0 ? totalCleaningCostB / validCleaningUsersB.length : 0;
                    const avgBedsheetCostB =
                      validBedsheetUsersB.length > 0 ? totalBedsheetCostB / validBedsheetUsersB.length : 0;
                    const vatB = 1 + cleanerSettings?.VAT / 100;
                    const totalVatB = (avgBedsheetCostB + avgCleaningCostB) * vatB;
                    // const cleaningCostB =
                    //   cleaningAmount.find((item) => item.BnBShortName === b[0])?.cleaning_fee_amount || 0;

                    const cleaningCostB =
                      usersB?.[0]?.bnbDetails?.bookingCleaningFee > 0
                        ? usersB[0].bnbDetails.bookingCleaningFee
                        : usersB?.[0]?.bnbDetails?.midStayCleaningFee > 0
                        ? usersB[0].bnbDetails.midStayCleaningFee
                        : cleaningAmount.find((item) => item.BnBShortName === b[0])?.cleaning_fee_amount || 0;

                    const costDifferenceB = cleaningCostB - totalVatB;

                    if (orderBy === 'costDifference' && order === 'desc') {
                      return costDifferenceB - costDifferenceA; // ascending
                    }

                    return costDifferenceA - costDifferenceB; // ascending
                  })
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map(([bnb, users]) => {
                    const validCleaningUsers = users.filter(
                      (user) => user.cleaningCost > cleanerSettings?.ignoreAverageCalValue
                    );
                    const validBedsheetUsers = users.filter(
                      (user) => user.bedsheetCost > cleanerSettings?.ignoreAverageCalValue
                    );

                    const totalCleaningCost = validCleaningUsers.reduce((acc, curr) => acc + curr.cleaningCost, 0);
                    const totalBedsheetCost = validBedsheetUsers.reduce((acc, curr) => acc + curr.bedsheetCost, 0);

                    const avgCleaningCost =
                      validCleaningUsers.length > 0
                        ? (totalCleaningCost / validCleaningUsers.length).toFixed(2)
                        : '0.00';

                    const avgBedsheetCost =
                      validBedsheetUsers.length > 0
                        ? (totalBedsheetCost / validBedsheetUsers.length).toFixed(2)
                        : '0.00';

                    const vat = 1 + cleanerSettings?.VAT / 100;
                    const totalVat = (+avgBedsheetCost + +avgCleaningCost) * vat;

                    const cleaningCost =
                      users?.[0]?.bnbDetails?.bookingCleaningFee > 0
                        ? users[0].bnbDetails.bookingCleaningFee
                        : users?.[0]?.bnbDetails?.midStayCleaningFee > 0
                        ? users[0].bnbDetails.midStayCleaningFee
                        : cleaningAmount.find((item) => item.BnBShortName === bnb)?.cleaning_fee_amount || 0;

                    console.log(bnb, users);

                    // const cleaningCost =
                    //   cleaningAmount.find((item) => item.BnBShortName === bnb)?.cleaning_fee_amount || 0;
                    const costDifference = (cleaningCost - totalVat).toFixed(2);

                    // const percentageDifference = calculatePercentageDifference(cleaningCost, totalVat);
                    const percentageDifference = calculatePercentageDifference(cleaningCost, costDifference, totalVat);

                    const rowColor = getColorByDifference(percentageDifference);

                    return (
                      <TableRow hover key={bnb} style={{ backgroundColor: rowColor }}>
                        <TableCell align="left">{bnb}</TableCell>
                        <TableCell align="left">{avgCleaningCost} €</TableCell>
                        <TableCell align="left">{avgBedsheetCost} €</TableCell>
                        <TableCell align="left">{totalVat.toFixed(2)} €</TableCell>
                        <TableCell align="left">{cleaningCost} €</TableCell>
                        <TableCell align="left">{costDifference} €</TableCell>
                        <TableCell align="left">{percentageDifference} %</TableCell>
                        <TableCell align="left">
                          <Button size="small" variant="contained" onClick={() => openBnbReports(bnb)}>
                            Dettaglio
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
                {loading && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8}>
                      <CircularProgress size="20px" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                      <Paper
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h6" paragraph>
                          Not found
                        </Typography>

                        <Typography variant="body2">
                          No results found for &nbsp;
                          <strong>&quot;{filterName}&quot;</strong>.
                          <br /> Try checking for typos or using complete words.
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[50, 100, 200]}
          component="div"
          count={groupedBnbArray.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </>
  );
}

const calculatePercentageDifference = (cleaningCost, costDifference, totalVat) => {
  if (totalVat === 0 && cleaningCost > 0) {
    return '100.00';
  }

  if (cleaningCost === 0) {
    if (totalVat === 0) {
      return '0.00'; // No cost, no VAT
    }
    return '-100.00'; // VAT exists, but cost is zero
  }

  // const difference = ((cleaningCost - totalVat) / cleaningCost) * 100;
  const difference = (costDifference / totalVat) * 100;
  return difference.toFixed(2);
};
